
import {
  defineComponent, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import BaseDropdown from '@/components/fields/partials/BaseDropdown.vue';
import BaseDropdownMultiple from '@/components/fields/partials/BaseDropdownMultiple.vue';
import Tooltip from 'primevue/tooltip';
import { OVERRIDABLE_FIELDS } from '@/composables/custom-data/overridableSystemFields';
import {
  Field, SystemSchema, ForeignKeyRow,
  SystemSchemaField,
} from '@/composables/custom-data/customData.interfaces';
import { useStore } from '@/store';
import { showToastError } from '@/helpers';
import SchemaOverridesEditor from './SchemaOverridesEditor.vue';

export default defineComponent({
  name: 'SchemaRelationsEditor',

  components: {
    BaseDropdownMultiple,
    BaseDropdown,
    SchemaOverridesEditor,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const idCustomDataDefinition = computed(() => store.getters['customObject/getIdCustomDataDefinition']);
    const fields = computed(() => store.getters['customObject/getFields']);
    const relationships = computed(() => store.getters['customObject/getRelationships']);
    const currentEditingElement = computed(() => store.getters['customObject/getCurrentEditingElement']);
    const neededData = computed(() => store.getters['customObject/getNeededData']);
    const existingRelationships = computed(() => store.getters['customObject/getNeededDataExistingRelationships'] || []);
    const overrides = computed(() => store.getters['customObject/getOverrides'] || []);

    const isRelationshipEditable = (relationship: ForeignKeyRow): boolean => !existingRelationships.value.map((r: any) => r.id).includes(relationship.id);

    const relationshipOnEdit = (relationship: ForeignKeyRow): boolean => currentEditingElement.value
     && currentEditingElement.value.id === relationship.id
      && currentEditingElement.value.elementType === 'relation';

    const isNewAddRelationship = (relationship: ForeignKeyRow): boolean => currentEditingElement.value
     && currentEditingElement.value.id === relationship.id
      && currentEditingElement.value.elementType === 'relation'
       && currentEditingElement.value.isNew;

    const addNewEmptyRelation = () => {
      if (currentEditingElement.value !== null) {
        showToastError(t('customData.relationEditor.actions.addEmptyRelation'), 4000);
        return;
      }
      store.dispatch('customObject/handleRelationAction', { action: 'add-new-empty' });
    };

    const removeRelation = (id: number) => store.dispatch('customObject/handleRelationAction', { action: 'remove', id });

    const editRelation = (id: number) => {
      if (currentEditingElement.value !== null) {
        showToastError(t('customData.relationEditor.actions.editRelation'), 4000);
        return;
      }
      store.dispatch('customObject/handleRelationAction', { action: 'edit', id });
    };

    const cancelEditRelation = (id: number) => store.dispatch('customObject/handleRelationAction', { action: 'cancel-edit', id });

    const saveRelation = (id: number) => {
      const fkRow = relationships.value.find((fk: ForeignKeyRow) => fk.id === id);
      if (fkRow) {
        if (!fkRow.localField || !fkRow.schema || !fkRow.field) {
          showToastError(t('customData.relationEditor.actions.saveRelation'), 4000);
          return;
        }
        store.dispatch('customObject/handleRelationAction', { action: 'save', id });
      }
    };

    const findFieldIndexByName = (name: string): number => fields.value.findIndex((f: Field) => f.name === name);

    const getAllowedLocalFieldTypes = (fk: ForeignKeyRow): string[] => {
      if (fk.schema_type === 'custom' && fk.schema) {
        const schema = findCustomSchema(fk.schema);
        if (schema?.fields?.length > 0) {
          return [...new Set(schema.fields.map((f: SystemSchemaField) => f.type))] as string[];
        }
        return ['number'];
      }
      if (fk.schema_type === 'system' && fk.schema) {
        const schema = neededData.value.systemSchemas.find((s: SystemSchema) => s.name === fk.schema);
        if (schema?.fields?.length > 0) {
          return [...new Set(schema.fields.map((f: SystemSchemaField) => f.type))] as string[];
        }
      }
      return ['text', 'number'];
    };

    const eligibleLocalFields = (fkBeingEdited: ForeignKeyRow): Field[] => {
      const usedLocalFields = new Set(
        relationships.value
          .filter((fk: ForeignKeyRow) => fk.id !== fkBeingEdited.id && !!fk.localField)
          .map((fk: ForeignKeyRow) => fk.localField),
      );
      // Fields already used as custom_field_name in overrides must be excluded
      const usedInOverrides = new Set(
        overrides.value
          .filter((o: any) => !!o.custom_field_name)
          .map((o: any) => o.custom_field_name),
      );
      // Only filter by type if the target schema is already selected
      const allowedTypes = fkBeingEdited.schema
        ? getAllowedLocalFieldTypes(fkBeingEdited)
        : null;
      return fields.value.filter((field: Field) => {
        // Type check: text or number fields only; restrict to target-compatible types if target is known
        if (field.type !== 'text' && field.type !== 'number') return false;
        if (allowedTypes && !allowedTypes.includes(field.type)) return false;
        if (usedInOverrides.has(field.name)) return false;
        return !usedLocalFields.has(field.name) || field.name === fkBeingEdited.localField;
      });
    };

    const targetSchemaOptions = computed((): any[] => {
      const usedTargetSchemas = new Set(
        relationships.value
          .filter((fk: ForeignKeyRow) => !relationshipOnEdit(fk) && fk.schema)
          .map((fk: ForeignKeyRow) => `${fk.schema_type}::${fk.schema}`),
      );

      const options: any[] = [];
      if (neededData.value.systemSchemas.length > 0) {
        const systemItems = neededData.value.systemSchemas
          .filter((s: any) => !usedTargetSchemas.has(`system::${s.name}`))
          .map((s: any) => ({ label: s.label, value: { type: 'system' as const, name: s.name } }));
        if (systemItems.length > 0) {
          options.push({ groupLabel: t('customData.relationEditor.systemSchemaLabel'), items: systemItems });
        }
      }
      if (neededData.value.customSchemas.length > 0) {
        let customItems = neededData.value.customSchemas
          .filter((s: any) => !usedTargetSchemas.has(`custom::${s.name}`));
        if (idCustomDataDefinition.value > 0) {
          customItems = customItems.filter((s: any) => parseInt(s.id_custom_data_definition, 10) !== parseInt(idCustomDataDefinition.value.toString(), 10));
        }
        customItems = customItems.map((s: any) => ({ label: s.label, value: { type: 'custom' as const, name: s.name } }));
        if (customItems.length > 0) {
          options.push({ groupLabel: t('customData.relationEditor.customSchemaLabel'), items: customItems });
        }
      }
      return options;
    });

    // Helper: find a custom schema by table_name OR id_custom_data_definition
    const findCustomSchema = (schemaRef: string): any | undefined => neededData.value.customSchemas.find(
      (s: any) => s.name === schemaRef || String(s.id_custom_data_definition) === String(schemaRef),
    );

    // Noms de champs cibles distincts d'un schéma système (les fields sont
    // dupliqués par type — ex. product_id text + product_id number).
    const systemTargetFieldNames = (schemaName: string): string[] => {
      const schema = neededData.value.systemSchemas.find((s: SystemSchema) => s.name === schemaName);
      const names = (schema?.fields || []).map((f: SystemSchemaField) => String(f.name));
      return [...new Set<string>(names)];
    };

    const getFixedTargetField = (schemaType: string, schemaName: string): string | null => {
      if (schemaType === 'system') {
        const names = systemTargetFieldNames(schemaName);
        // Une seule cible → auto-assignée (comportement historique). Plusieurs
        // cibles (ex. contacts : id_contact / group_id / lang / country) →
        // l'utilisateur choisit, comme pour les schémas custom.
        if (names.length === 1) return names[0];
        return null;
      }
      if (schemaType === 'custom') {
        const schema = findCustomSchema(schemaName);
        // If custom schema has only 'id' (no single unique key), auto-assign
        if (!schema?.fields || schema.fields.length <= 1) return 'id';
        // Multiple target fields available → user must choose
        return null;
      }
      return null;
    };

    const hasMultipleTargetFields = (fk: ForeignKeyRow): boolean => {
      if (!fk.schema) return false;
      if (fk.schema_type === 'system') {
        return systemTargetFieldNames(fk.schema).length > 1;
      }
      const schema = findCustomSchema(fk.schema);
      return schema?.fields?.length > 1;
    };

    const onTargetSchemaChange = (fk: ForeignKeyRow) => {
      const mutableFk = relationships.value.find((item: ForeignKeyRow) => item.id === fk.id);
      if (!mutableFk) return;
      const selection = mutableFk.selectedTargetSchema;
      if (selection && selection.length > 0) {
        mutableFk.schema_type = selection[0].type;
        mutableFk.schema = selection[0].name;
        const fixedField = getFixedTargetField(selection[0].type, selection[0].name);
        mutableFk.field = fixedField || '';

        // Check if current localField is still compatible with the new target schema
        if (mutableFk.localField) {
          const localFieldObj = fields.value.find((f: Field) => f.name === mutableFk.localField);
          if (localFieldObj) {
            const allowedTypes = getAllowedLocalFieldTypes(mutableFk);
            if (!allowedTypes.includes(localFieldObj.type)) {
              // Type incompatible → reset localField so the user picks a compatible one
              console.warn(`[RelationsEditor] localField "${mutableFk.localField}" (type: ${localFieldObj.type}) incompatible with target schema (allowed types: ${allowedTypes.join(', ')}). Resetting localField.`);
              showToastError(t('customData.relationEditor.localFieldTypeIncompatible', { field: mutableFk.localField, type: localFieldObj.type, allowedTypes: allowedTypes.join(', ') }), 5000);
              mutableFk.localField = '';
            }
          }
        }
      } else {
        mutableFk.schema_type = 'system';
        mutableFk.schema = '';
        mutableFk.field = '';
      }
    };

    const onLocalFieldChange = (fk: ForeignKeyRow, newValue: string) => {
      const mutableFk = relationships.value.find((item: ForeignKeyRow) => item.id === fk.id);
      if (!mutableFk) return;
      // Guard: ignore null/undefined emitted by dropdown when options change (PrimeVue auto-clear)
      if (newValue === null || newValue === undefined) return;
      mutableFk.localField = newValue;
      // If target field is set but no longer compatible with the new local field type, reset it
      if (mutableFk.field && mutableFk.schema) {
        const compatible = availableTargetFields(mutableFk);
        if (!compatible.some((f) => f.name === mutableFk.field)) {
          // Auto-assign if only one option, otherwise clear for user to choose
          const fixedField = getFixedTargetField(mutableFk.schema_type, mutableFk.schema);
          mutableFk.field = fixedField || '';
        }
      }
    };

    const usedTargetFields = computed(
      () => relationships.value
        .filter((fk: ForeignKeyRow) => !relationshipOnEdit(fk) && fk.schema && fk.field)
        .map((fk: ForeignKeyRow) => `${fk.schema_type}::${fk.schema}::${fk.field}`),
    );

    const availableTargetFields = (fk: ForeignKeyRow): (SystemSchemaField & { disabled?: boolean })[] => {
      if (!fk.schema) return [];
      const targetSchema = fk.schema_type === 'system'
        ? neededData.value.systemSchemas.find((s: SystemSchema) => s.name === fk.schema)
        : findCustomSchema(fk.schema);
      if (!targetSchema?.fields) return [];

      // Filter target fields by local field type compatibility (if localField is selected)
      let potentialFields = targetSchema.fields;
      if (fk.localField) {
        const localFieldIndex = findFieldIndexByName(fk.localField);
        if (localFieldIndex !== -1) {
          const localFieldType = fields.value[localFieldIndex].type;
          potentialFields = targetSchema.fields.filter(
            (tf: SystemSchemaField) => tf.type === localFieldType,
          );
        }
      }

      return potentialFields.map((targetField: SystemSchemaField) => {
        const targetIdentifier = `${fk.schema_type}::${fk.schema}::${targetField.name}`;
        const isUsedByAnother = usedTargetFields.value.includes(targetIdentifier)
                         && !(fk.originalData?.schema === fk.schema && fk.originalData?.field === targetField.name);
        return { ...targetField, disabled: isUsedByAnother };
      });
    };

    const getTargetSchemaLabel = (relation: ForeignKeyRow): string => {
      if (!relation.schema) return '';
      const schemas = relation.schema_type === 'system' ? neededData.value.systemSchemas : neededData.value.customSchemas;
      if (relation.schema_type === 'system') {
        const targetSchema = schemas.find((s: SystemSchema) => s.name === relation.schema);
        return targetSchema?.label || relation.schema;
      }
      // Custom: relation.schema can be table_name OR id_custom_data_definition (from API)
      const targetSchema = findCustomSchema(relation.schema);
      if (targetSchema) {
        return `${targetSchema.label} #${targetSchema.id_custom_data_definition}`;
      }
      return relation.schema;
    };

    const getTargetFieldTooltip = (fk: ForeignKeyRow): string => {
      if (fk.schema_type === 'custom' && fk.field === 'id') {
        return t('customData.relationEditor.targetFieldTooltips.customId');
      }
      if (fk.schema_type === 'custom' && fk.field && fk.field !== 'id') {
        return t('customData.relationEditor.targetFieldTooltips.customUniqueKey', { field: fk.field });
      }
      if (fk.schema_type === 'system' && fk.field === 'product_id') {
        return t('customData.relationEditor.targetFieldTooltips.productId');
      }
      if (fk.schema_type === 'system' && fk.field === 'id_contact') {
        return t('customData.relationEditor.targetFieldTooltips.contactId');
      }
      return '';
    };

    const isRelationOverridable = (relation: ForeignKeyRow): boolean => relation.schema_type === 'system'
     && !!relation.schema
     && Object.keys(OVERRIDABLE_FIELDS).includes(relation.schema as string)
     && !relationshipOnEdit(relation);

    const getTargetFieldLabel = (fk: ForeignKeyRow): string => {
      if (!fk.schema || !fk.field) return fk.field || '';
      const schemas = fk.schema_type === 'system' ? neededData.value.systemSchemas : neededData.value.customSchemas;
      const targetSchema = schemas.find((s: SystemSchema) => s.name === fk.schema);
      if (targetSchema?.fields) {
        const targetField = targetSchema.fields.find((f: SystemSchemaField) => f.name === fk.field);
        return targetField?.label || fk.field;
      }
      if (fk.schema_type === 'custom' && fk.field === 'id') {
        return 'id';
      }
      return fk.field;
    };

    return {
      t,
      relationships,
      isRelationshipEditable,
      relationshipOnEdit,
      isNewAddRelationship,
      eligibleLocalFields,
      targetSchemaOptions,
      onTargetSchemaChange,
      onLocalFieldChange,
      availableTargetFields,
      hasMultipleTargetFields,
      addNewEmptyRelation,
      removeRelation,
      editRelation,
      saveRelation,
      cancelEditRelation,
      getTargetFieldLabel,
      getTargetFieldTooltip,
      getTargetSchemaLabel,
      isRelationOverridable,
    };
  },
});
